import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import { getAll, getByDoubleQuery, getById, getBySingleQuery, remove, update, updateField, updateFields } from '../utils/dbUtils';
import moment from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import { collection, doc, getCountFromServer, getDocs, increment, query, writeBatch, orderBy, limitToLast, limit, startAfter, endBefore, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { asTimeZone } from '../utils/dateFormatter';

class Activity {
	static collectionName = 'activities';

	coveredEntityId = null;
	userId = null;
	type = 'NONE'; // EMAIL, SMS, CALL, FACE, NONE
	direction = 'OUTGOING'; // INCOMING, OUTGOING
	details = null;
	outcome = null;
	important = false;
	messageId = null;
	callId = null;
	emailId = null;
	templateId = null; // smsTemplateId or emailTemplateId
	metadata = {}; // can store additional fields such as callDurationSec, recordingUrl, callSid, fromPhone, callSummary, callScore, customerSentiment, etc.

	constructor(activity) {
		if (isObjectNotEmpty(activity)) {
			if (isStringNotEmpty(activity.coveredEntityId)) { this.coveredEntityId = activity.coveredEntityId; }
			if (isStringNotEmpty(activity.userId)) { this.userId = activity.userId; }
			if (isStringNotEmpty(activity.type)) { this.type = activity.type; }
			if (isStringNotEmpty(activity.direction)) { this.direction = activity.direction; }
			if (isStringNotEmpty(activity.details)) { this.details = activity.details; }
			if (isStringNotEmpty(activity.outcome)) { this.outcome = activity.outcome; }
			if (isBooleanTrue(activity.important)) { this.important = true; }
			if (isStringNotEmpty(activity.messageId)) { this.messageId = activity.messageId; }
			if (isStringNotEmpty(activity.callId)) { this.callId = activity.callId; }
			if (isStringNotEmpty(activity.emailId)) { this.emailId = activity.emailId; }
			if (isStringNotEmpty(activity.templateId)) { this.templateId = activity.templateId; }
			if (isObjectNotEmpty(activity.metadata)) { this.metadata = activity.metadata; }
		}
	}

	toDto = () => {
		return {
			coveredEntityId: this.coveredEntityId,
			userId: this.userId,
			type: this.type,
			direction: this.direction,
			details: this.details,
			outcome: this.outcome,
			important: this.important,
			messageId: this.messageId,
			callId: this.callId,
			emailId: this.emailId,
			templateId: this.templateId,
			metadata: this.metadata
		};
	}

	create = async () => {
		const batch = writeBatch(db);

		let activityId = uuid();
		let newActivity = this.toDto();
		newActivity.createdAt = moment().utc().toDate();
		newActivity.createdBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
		newActivity.modifiedAt = moment().utc().toDate();
		newActivity.modifiedBy = isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM';
		newActivity.isDeleted = false;

		const activityRef = doc(collection(db, Activity.collectionName), activityId);
		batch.set(activityRef, newActivity);

		const userRef = doc(collection(db, 'users'), this.userId);

		batch.update(userRef, {
			activityCount: increment(1),
		});

		if (this.type === 'CALL') {
			batch.update(userRef, {
				callCount: increment(1)
			});
		} else if (this.type === 'SMS') {
			batch.update(userRef, {
				smsCount: increment(1)
			});
		} else if (this.type === 'EMAIL') {
			batch.update(userRef, {
				emailCount: increment(1)
			});
		} else if (this.type === 'FACE') {
			batch.update(userRef, {
				faceCount: increment(1)
			});
		}

		// Commit the batch
		await batch.commit();

		return { ...newActivity, id: activityId };
	}

	static updateField = async (id, fieldName, newValue) => {
		if (isStringNotEmpty(id)) {
			return await updateField(Activity.collectionName, id, fieldName, newValue);
		} else {
			throw new Error("id is required.");
		}
	}

	static updateFields = async (id, updatedFields) => {
		if (isStringNotEmpty(id)) {
			return await updateFields(Activity.collectionName, id, updatedFields);
		} else {
			throw new Error("id is required.");
		}
	}

	static update = async (id, updatedActivity) => {
		const updatedObj = new Activity(updatedActivity);
		if (id === updatedActivity.id) {
			return await update(Activity.collectionName, id, updatedObj.toDto());
		} else {
			throw new Error('id and id field of the updated activity must match.')
		}
	}

	static delete = async (id) => {
		if (isStringNotEmpty(id)) {
			return await remove(Activity.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static deleteByMessageId = async (messageId) => {
		if (isStringNotEmpty(messageId)) {
			let activities = await this.getByField('messageId', messageId, '==');
			if (isListNotEmpty(activities)) {
				// console.log(activities.length);
				let activityToDelete = activities[0];
				return await remove(Activity.collectionName, activityToDelete.id);
			}

			return false;
		} else {
			throw new Error("messageId is required.");
		}
	}

	static getById = async (id) => {
		if (isStringNotEmpty(id)) {
			return await getById(Activity.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getByCoveredEntityId = async (coveredEntityId) => {
		if (isStringNotEmpty(coveredEntityId)) {
			return await getBySingleQuery(Activity.collectionName, 'coveredEntityId', coveredEntityId, '==');
		} else {
			throw new Error("coveredEntityId is required.");
		}
	}

	static getByUserId = async (userId) => {
		if (isStringNotEmpty(userId)) {
			return await getBySingleQuery(Activity.collectionName, 'userId', userId, '==');
		} else {
			throw new Error("userId is required.");
		}
	}

	static getByCoveredEntityIdAndUserId = async (coveredEntityId, userId) => {
		if (isStringNotEmpty(coveredEntityId) && isStringNotEmpty(userId)) {
			return await getByDoubleQuery(Activity.collectionName, 'coveredEntityId', coveredEntityId, '==', 'userId', userId, '==');
		} else {
			throw new Error("coveredEntityId and userId are required.");
		}
	}

	static getAll = async () => {
		return await getAll(Activity.collectionName);
	}

	static getByField = async (fieldName, fieldValue, comparer = '==') => {
		return await getBySingleQuery(Activity.collectionName, fieldName, fieldValue, comparer);
	}

	static getByCoveredEntityIdAndUserIdAndType = async (coveredEntityId, userId, type) => {
		let queryConstraints = [];

		if (isListNotEmpty(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
		}

		if (isListNotEmpty(type)) {
			queryConstraints.push(where('type', 'in', type));
		} else if (isStringNotEmptyOrZeroLength(type)) {
			queryConstraints.push(where('type', '==', type));
		}

		if (isListNotEmpty(userId)) {
			queryConstraints.push(where('userId', 'in', userId));
		} else if (isStringNotEmptyOrZeroLength(userId)) {
			queryConstraints.push(where('userId', '==', userId));
		}

		let q = query(collection(db, Activity.collectionName), ...queryConstraints);

		let snapShot = await getDocs(q);
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static getActivityCount = async (coveredEntityId, type, direction, outcome, important, start, end) => {
		let queryConstraints = [];

		if (isListNotEmpty(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
		}

		if (isListNotEmpty(type)) {
			queryConstraints.push(where('type', 'in', type));
		} else if (isStringNotEmptyOrZeroLength(type)) {
			queryConstraints.push(where('type', '==', type));
		}

		if (isListNotEmpty(direction)) {
			queryConstraints.push(where('direction', 'in', direction));
		} else if (isStringNotEmptyOrZeroLength(direction)) {
			queryConstraints.push(where('direction', '==', direction));
		}

		if (isListNotEmpty(outcome)) {
			queryConstraints.push(where('outcome', 'in', outcome));
		} else if (isStringNotEmptyOrZeroLength(outcome)) {
			queryConstraints.push(where('outcome', '==', outcome));
		}

		if (isNotNullOrUndefined(important)) {
			queryConstraints.push(where('important', '==', isBooleanTrue(important)));
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = query(collection(db, Activity.collectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getActivitiesPaginated = async (coveredEntityId, type, direction, outcome, important, start, end, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
		let queryConstraints = [];

		if (isListNotEmpty(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', 'in', coveredEntityId));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
			queryConstraints.push(where('coveredEntityId', '==', coveredEntityId));
		}

		if (isListNotEmpty(type)) {
			queryConstraints.push(where('type', 'in', type));
		} else if (isStringNotEmptyOrZeroLength(type)) {
			queryConstraints.push(where('type', '==', type));
		}

		if (isListNotEmpty(direction)) {
			queryConstraints.push(where('direction', 'in', direction));
		} else if (isStringNotEmptyOrZeroLength(direction)) {
			queryConstraints.push(where('direction', '==', direction));
		}

		if (isListNotEmpty(outcome)) {
			queryConstraints.push(where('outcome', 'in', outcome));
		} else if (isStringNotEmptyOrZeroLength(outcome)) {
			queryConstraints.push(where('outcome', '==', outcome));
		}

		if (isNotNullOrUndefined(important)) {
			queryConstraints.push(where('important', '==', isBooleanTrue(important)));
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = null;
		if (isObjectNotEmpty(firstVisible)) {
			q = query(collection(db, Activity.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
		} else if (isObjectNotEmpty(lastVisible)) {
			q = query(collection(db, Activity.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
		} else {
			q = query(collection(db, Activity.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
		}

		let snapShot = await getDocs(q);
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static getActivities = async (coveredEntityId, type, direction, outcome, important, start, end) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await Activity.getActivityCount(coveredEntityId, type, direction, outcome, important, start, end);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await Activity.getActivitiesPaginated(coveredEntityId, type, direction, outcome, important, start, end, 10000, null, next, 'createdAt');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default Activity;